/**
 * src/components/shared/SmartSearchOverlay.jsx — Smart Search Overlay
 *
 * Ctrl+K / Cmd+K to toggle. Calls brain.search API with 600ms debounce.
 * Fixed overlay with backdrop blur, styled results card.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Search, X, Loader2 } from 'lucide-react';
import { apiFetch, API_BASE } from '../../lib/api/client';
import { useT } from '../../lib/i18n/I18nContext';

// Recent searches — last N queries persisted to localStorage so reopening
// the overlay shows quick-pick chips. Feature request 2026-04-08 PM.
const RECENT_KEY = 'acn-recent-searches';
const RECENT_MAX = 5;

function loadRecentSearches() {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(0, RECENT_MAX) : [];
  } catch { return []; }
}

function pushRecentSearch(q) {
  try {
    const trimmed = String(q || '').trim();
    if (trimmed.length < 3) return loadRecentSearches();
    const current = loadRecentSearches();
    // Dedupe case-insensitively, newest first, cap at RECENT_MAX
    const filtered = current.filter(s => s.toLowerCase() !== trimmed.toLowerCase());
    const next = [trimmed, ...filtered].slice(0, RECENT_MAX);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
    return next;
  } catch { return loadRecentSearches(); }
}

/**
 * variant (TICKET-107 c):
 *   'icon'  — bare magnifier button (default; mobile top bar + tablet rail,
 *             tap-to-expand per CEO-approved UX 2026-06-12)
 *   'field' — always-visible "finding bar": a search-field affordance that
 *             opens the overlay on click (desktop sidebar)
 */
export default function SmartSearchOverlay({ variant = 'icon' }) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [answer, setAnswer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [recent, setRecent] = useState(() => loadRecentSearches());
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // Keyboard shortcut: Ctrl+K / Cmd+K to toggle, Escape to close
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
      if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open]);

  // Focus input when overlay opens
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
    if (!open) {
      setQuery('');
      setAnswer(null);
      setSearched(false);
      setLoading(false);
    }
  }, [open]);

  // Debounced search
  const doSearch = useCallback(async (q) => {
    if (q.length < 3) {
      setAnswer(null);
      setSearched(false);
      return;
    }
    setLoading(true);
    setSearched(true);
    try {
      // brain.search lives in /api/data (consolidated router).
      // Returns either { answer: string } (LightRAG) or
      // { source: 'supabase-fallback', tasks, projects, bundles, people } (fallback).
      const res = await apiFetch(`${API_BASE}/data`, {
        method: 'POST',
        body: JSON.stringify({ action: 'brain.search', query: q }),
      });
      if (res && res.source === 'supabase-fallback') {
        setAnswer(res);
      } else if (res && res.answer) {
        setAnswer(res.answer);
      } else if (res && res.result) {
        setAnswer(res.result);
      } else {
        setAnswer(null);
      }
      // Record this query in recent history (deduped, capped).
      setRecent(pushRecentSearch(q));
    } catch (err) {
      console.warn('[SmartSearch] Error:', err.message);
      setAnswer(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Click a recent chip: populate input + fire search immediately (no debounce).
  const runRecent = useCallback((q) => {
    setQuery(q);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    doSearch(q);
  }, [doSearch]);

  const handleInput = useCallback((e) => {
    const val = e.target.value;
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), 600);
  }, [doSearch]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  if (!open) {
    if (variant === 'field') {
      return (
        <button
          data-testid="smart-search-field"
          onClick={() => setOpen(true)}
          title="Ctrl+K"
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            width: '100%', minHeight: 44,
            padding: '8px 12px', borderRadius: 10,
            background: 'var(--bg)',
            border: 'none', outline: '1px solid var(--bdr)',
            color: 'var(--tm)', fontSize: 13,
            cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
          }}
        >
          <Search size={16} color="var(--tm)" strokeWidth={1.5} style={{ flexShrink: 0 }} />
          <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {t('search.finding')}
          </span>
        </button>
      );
    }
    return (
      <button
        data-testid="smart-search-btn"
        onClick={() => setOpen(true)}
        title="Search (Ctrl+K)"
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 36, height: 36, borderRadius: '50%',
          background: 'transparent',
          border: 'none', cursor: 'pointer',
          padding: 0, flexShrink: 0,
          minHeight: 44, minWidth: 44,
        }}
      >
        <Search size={18} color="var(--tm)" strokeWidth={1.5} />
      </button>
    );
  }

  // Portal the open-state panel to document.body so it escapes any ancestor
  // stacking context (notably the sidebar's `backdrop-filter`, which traps
  // fixed-positioned descendants and clamps the panel to 240px wide). The
  // trigger button above stays inline wherever the component is rendered.
  return createPortal(
    <>
      {/* Backdrop */}
      <div
        onClick={() => setOpen(false)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.target !== e.currentTarget) return; if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); (() => setOpen(false))(e); } }}
        style={{
          position: 'fixed', inset: 0, zIndex: 9998,
          background: 'rgba(0,0,0,0.4)',
        }}
      />

      {/* Panel */}
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
        background: 'var(--bg)',
        borderBottom: '1px solid var(--bdr)',
        boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
        padding: '12px 16px 16px',
        // Drop below the iOS status bar / Dynamic Island in standalone PWA
        // (viewport-fit=cover) so the input + hint aren't hidden under the pill.
        paddingTop: 'calc(12px + env(safe-area-inset-top, 0px))',
      }}>
        {/* Search input row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Search size={18} color="var(--tm)" strokeWidth={1.5} style={{ flexShrink: 0 }} />
          <input
            ref={inputRef}
            data-testid="smart-search-input"
            type="text"
            value={query}
            onChange={handleInput}
            placeholder="Search everything..."
            style={{
              flex: 1, fontSize: 16, fontFamily: 'inherit',
              background: 'transparent', border: 'none', outline: 'none',
              color: 'var(--t1)', minHeight: 44, padding: '8px 0',
            }}
          />
          {loading && (
            <Loader2
              size={18} color="var(--accent)" strokeWidth={1.5}
              style={{ animation: 'smartSearchSpin 1s linear infinite', flexShrink: 0 }}
            />
          )}
          <button
            onClick={() => setOpen(false)}
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 36, height: 36, borderRadius: '50%',
              background: 'transparent', border: 'none', cursor: 'pointer',
              padding: 0, flexShrink: 0,
              minHeight: 44, minWidth: 44,
            }}
          >
            <X size={18} color="var(--tm)" strokeWidth={1.5} />
          </button>
        </div>

        {/* Recent searches — shown only when the input is empty and there
            is recent history. Click a chip to re-run. Added 2026-04-08 PM
            per CEO request: "when you click on a task and search again,
            give last search queries so you avoid typing again." */}
        {query.length === 0 && recent.length > 0 && (
          <div style={{ marginTop: 10, paddingLeft: 28, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
            <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--tm)', textTransform: 'uppercase', letterSpacing: 0.4, marginRight: 4 }}>Recent</span>
            {recent.map((q) => (
              <button
                key={q}
                onClick={() => runRecent(q)}
                data-testid={`smart-search-recent-${q}`}
                style={{
                  padding: '6px 12px', borderRadius: 99, fontSize: 12, fontWeight: 500,
                  background: 'var(--glass)', color: 'var(--t2)',
                  border: '1px solid var(--bdr)', cursor: 'pointer',
                  minHeight: 32,
                  transition: 'background 120ms, color 120ms, border-color 120ms',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'var(--accent-bg)'; e.currentTarget.style.color = 'var(--accent)'; e.currentTarget.style.borderColor = 'var(--accent)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'var(--glass)'; e.currentTarget.style.color = 'var(--t2)'; e.currentTarget.style.borderColor = 'var(--bdr)'; }}
              >{q}</button>
            ))}
            <button
              onClick={() => { localStorage.removeItem(RECENT_KEY); setRecent([]); }}
              title="Clear recent"
              style={{
                padding: '6px 8px', borderRadius: 99, fontSize: 10,
                background: 'transparent', color: 'var(--tm)',
                border: 'none', cursor: 'pointer', minHeight: 32,
              }}
            >Clear</button>
          </div>
        )}

        {/* Keyboard hint */}
        <div style={{ fontSize: 11, color: 'var(--tm)', marginTop: 4, paddingLeft: 28 }}>
          <kbd style={{
            padding: '1px 5px', borderRadius: 4, fontSize: 10,
            background: 'var(--stodob)', border: '1px solid var(--bdr)',
          }}>
            {navigator.platform?.includes('Mac') ? 'Cmd' : 'Ctrl'}+K
          </kbd>
          {' '}to toggle &middot;{' '}
          <kbd style={{
            padding: '1px 5px', borderRadius: 4, fontSize: 10,
            background: 'var(--stodob)', border: '1px solid var(--bdr)',
          }}>Esc</kbd>
          {' '}to close
        </div>

        {/* Results — LightRAG prose answer */}
        {answer && typeof answer === 'string' && (
          <div style={{
            marginTop: 12, padding: '14px 16px',
            background: 'var(--glass)', border: '1px solid var(--bdr)',
            borderRadius: 12, maxHeight: '60vh', overflowY: 'auto',
            whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.6,
            color: 'var(--t1)',
          }}>
            {answer}
          </div>
        )}

        {/* Results — Supabase fallback: structured categories, clickable.
            Clicking an item closes the overlay and dispatches an
            `acn:search-open` CustomEvent that AppShell + target views listen
            for. AppShell switches the active tab, target view opens the
            specific entity (e.g. FocusView opens the TaskDetailPanel for a
            matching task id). This is the same event pattern used by
            `open-agent-chat` and `view-context-change` elsewhere in the app.
            Covers: tasks, projects, bundles, people, deals (Pipeline tab),
            contacts, vessels (Directory tab). RLS-scoped by SECURITY INVOKER RPC. */}
        {answer && typeof answer === 'object' && answer.source === 'supabase-fallback' && (() => {
          const { tasks = [], projects = [], bundles = [], people = [], deals = [], contacts = [], vessels = [], answer: prose } = answer;
          const total = tasks.length + projects.length + bundles.length + people.length + deals.length + contacts.length + vessels.length;
          // LightRAG prose rides ALONG with the structured sections (CEO 2026-07-02: "silvana
          // looked like a google answer"): show it as a banner ABOVE the clickable, deep-linkable
          // results — never instead of them.
          const proseText = typeof prose === 'string' ? prose.trim() : '';
          if (total === 0 && !proseText) return null;
          const proseStyle = {
            marginTop: 12, padding: '14px 16px',
            background: 'var(--glass)', border: '1px solid var(--bdr)',
            borderRadius: 12, maxHeight: '32vh', overflowY: 'auto',
            whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.6, color: 'var(--t1)',
          };
          const categoryStyle = {
            marginTop: 12, background: 'var(--glass)', border: '1px solid var(--bdr)',
            borderRadius: 12, overflow: 'hidden', maxHeight: '60vh', overflowY: 'auto',
          };
          const headerStyle = {
            fontSize: 10, fontWeight: 700, color: 'var(--tm)', letterSpacing: '0.08em',
            textTransform: 'uppercase', padding: '8px 14px 4px',
          };
          const itemStyle = {
            padding: '10px 14px', fontSize: 13, color: 'var(--t1)',
            borderTop: '1px solid var(--bdr)', cursor: 'pointer',
            minHeight: 44, display: 'flex', alignItems: 'center', gap: 8,
            transition: 'background 120ms',
          };
          const metaStyle = { fontSize: 11, color: 'var(--tm)', flexShrink: 0 };
          const openItem = (type, id) => {
            setOpen(false);
            setQuery('');
            setAnswer(null);
            setSearched(false);
            // Dispatch on next tick so the overlay unmount doesn't race with
            // the target view's modal-open state change.
            setTimeout(() => {
              window.dispatchEvent(new CustomEvent('acn:search-open', { detail: { type, id } }));
            }, 0);
          };
          return (
            <>
              {proseText && (
                <div data-testid="search-answer-prose" style={proseStyle}>{proseText}</div>
              )}
              {total > 0 && (
              <div style={categoryStyle} data-testid="search-fallback-results">
              {tasks.length > 0 && <>
                <div style={headerStyle}>Tasks</div>
                {tasks.map(t => (
                  <div
                    key={t.id}
                    data-testid={`search-result-task-${t.id}`}
                    style={itemStyle}
                    role="button"
                    tabIndex={0}
                    onClick={() => openItem('task', t.id)}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openItem('task', t.id); } }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >{t.task_name}</div>
                ))}
              </>}
              {projects.length > 0 && <>
                <div style={headerStyle}>Projects</div>
                {projects.map(p => (
                  <div
                    key={p.id}
                    data-testid={`search-result-project-${p.id}`}
                    style={itemStyle}
                    role="button"
                    tabIndex={0}
                    onClick={() => openItem('project', p.id)}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openItem('project', p.id); } }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >{p.project_name}</div>
                ))}
              </>}
              {bundles.length > 0 && <>
                <div style={headerStyle}>Bundles</div>
                {bundles.map(b => (
                  <div
                    key={b.id}
                    data-testid={`search-result-bundle-${b.id}`}
                    style={itemStyle}
                    role="button"
                    tabIndex={0}
                    onClick={() => openItem('bundle', b.id)}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openItem('bundle', b.id); } }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >{b.title}</div>
                ))}
              </>}
              {people.length > 0 && <>
                <div style={headerStyle}>People</div>
                {people.map(p => (
                  <div
                    key={p.id}
                    data-testid={`search-result-person-${p.id}`}
                    style={itemStyle}
                    role="button"
                    tabIndex={0}
                    onClick={() => openItem('person', p.id)}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openItem('person', p.id); } }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >{p.display_name}</div>
                ))}
              </>}
              {deals.length > 0 && <>
                <div style={headerStyle}>Pipeline</div>
                {deals.map(d => (
                  <div
                    key={d.id}
                    data-testid={`search-result-deal-${d.id}`}
                    style={itemStyle}
                    role="button"
                    tabIndex={0}
                    onClick={() => openItem('deal', d.id)}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openItem('deal', d.id); } }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.opportunity}</span>
                    {d.status && <span style={metaStyle}>{d.status}</span>}
                  </div>
                ))}
              </>}
              {contacts.length > 0 && <>
                <div style={headerStyle}>Contacts</div>
                {contacts.map(c => (
                  <div
                    key={c.id}
                    data-testid={`search-result-contact-${c.id}`}
                    style={itemStyle}
                    role="button"
                    tabIndex={0}
                    onClick={() => openItem('contact', c.id)}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openItem('contact', c.id); } }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.contact_name}</span>
                    {c.email && <span style={metaStyle}>{c.email}</span>}
                  </div>
                ))}
              </>}
              {vessels.length > 0 && <>
                <div style={headerStyle}>Boats</div>
                {vessels.map(v => (
                  <div
                    key={v.id}
                    data-testid={`search-result-vessel-${v.id}`}
                    style={itemStyle}
                    role="button"
                    tabIndex={0}
                    onClick={() => openItem('vessel', v.id)}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openItem('vessel', v.id); } }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.vessel_name}</span>
                    {v.make_model && <span style={metaStyle}>{v.make_model}</span>}
                  </div>
                ))}
              </>}
              </div>
              )}
            </>
          );
        })()}

        {/* No results state */}
        {!loading && searched && !answer && query.length >= 3 && (
          <div style={{
            marginTop: 12, padding: '14px 16px', textAlign: 'center',
            color: 'var(--tm)', fontSize: 14,
          }}>
            No results yet...
          </div>
        )}
      </div>

      <style>{`
        @keyframes smartSearchSpin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>,
    document.body,
  );
}
